// piano-frontend/src/pages/FeatureGuidePage.js
// 기능 안내 — 역할별 기능을 카테고리로 묶어 보여주고, 행을 누르면 해당 화면으로 이동한다.
import React, { useMemo, useState } from 'react';
import { Box, Typography, Chip, ButtonBase, Paper } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ChevronRight } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useMetronome } from '../metronome/MetronomeProvider';
import { FEATURES, FEATURE_CATEGORIES, isFeatureNew } from '../features/featureGuide';

const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;

function NewBadge() {
  return (
    <Box
      component="span"
      sx={(t) => ({
        ml: 0.75, px: 0.75, py: '1px', borderRadius: 0.75,
        fontSize: '0.65rem', fontWeight: 700, letterSpacing: 0.3, verticalAlign: 'middle',
        bgcolor: alpha(t.palette.secondary.main, t.palette.mode === 'dark' ? 0.22 : 0.16),
        color: t.palette.mode === 'dark' ? 'secondary.main' : 'secondary.dark',
      })}
    >
      NEW
    </Box>
  );
}

function FeatureRow({ feature, onAction }) {
  const Icon = feature.icon;
  const actionable = !!feature.action;
  const isNew = isFeatureNew(feature);

  const content = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, width: '100%', textAlign: 'left' }}>
      <Icon sx={{ fontSize: 24, color: 'text.secondary', flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {feature.title}
          {isNew && <NewBadge />}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.45, mt: 0.25 }}>
          {feature.desc}
        </Typography>
      </Box>
      {actionable ? (
        <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', flexShrink: 0 }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>열기</Typography>
          <ChevronRight sx={{ fontSize: 18 }} />
        </Box>
      ) : feature.hint ? (
        <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0, whiteSpace: 'nowrap', textAlign: 'right' }}>
          {feature.hint}
        </Typography>
      ) : null}
    </Box>
  );

  const rowSx = { display: 'block', width: '100%', '&:not(:first-of-type)': { borderTop: 1, borderColor: 'divider' } };

  return actionable ? (
    <ButtonBase onClick={() => onAction(feature.action)} sx={{ ...rowSx, '&:hover': { bgcolor: 'action.hover' } }}>
      {content}
    </ButtonBase>
  ) : (
    <Box sx={rowSx}>{content}</Box>
  );
}

export default function FeatureGuidePage({ user }) {
  const navigate = useNavigate();
  const metronome = useMetronome();
  const [category, setCategory] = useState('전체');

  const features = useMemo(() => {
    const installed = isStandalone();
    return FEATURES.filter((f) => f.roles.includes(user?.role) && !(f.hideWhenInstalled && installed));
  }, [user?.role]);

  const categories = useMemo(
    () => FEATURE_CATEGORIES.filter((c) => features.some((f) => f.category === c)),
    [features]
  );

  const handleAction = (action) => {
    if (action.type === 'metronome') metronome.openSheet();
    else if (action.type === 'route') navigate(action.to);
  };

  const visibleCategories = category === '전체' ? categories : [category];

  return (
    // 모바일은 좌우 여백 없이 목록을 화면 끝까지 붙여 폭을 확보한다(PC는 기존 여백 유지)
    <Box sx={{ pt: 2, pb: 2, px: { xs: 0, sm: 2 }, maxWidth: 600, mx: 'auto' }}>
      {/* 모바일은 헤더에 페이지 제목이 표시되므로 본문 제목 숨김 */}
      <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>기능 안내</Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 2, px: { xs: 2, sm: 0 } }}>
        {['전체', ...categories].map((c) => {
          const selected = c === category;
          return (
            <Chip
              key={c}
              label={c}
              size="small"
              onClick={() => setCategory(c)}
              variant={selected ? 'filled' : 'outlined'}
              color={selected ? 'primary' : 'default'}
              sx={{ fontWeight: 600 }}
            />
          );
        })}
      </Box>

      {visibleCategories.map((c) => (
        <Box key={c} sx={{ mb: 2.5 }}>
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 600, mb: 0.75, px: { xs: 2, sm: 0.5 } }}>
            {c}
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              overflow: 'hidden',
              borderRadius: { xs: 0, sm: 2 },
              borderLeftWidth: { xs: 0, sm: 1 },
              borderRightWidth: { xs: 0, sm: 1 },
            }}
          >
            {features.filter((f) => f.category === c).map((f) => (
              <FeatureRow key={f.id} feature={f} onAction={handleAction} />
            ))}
          </Paper>
        </Box>
      ))}
    </Box>
  );
}
